import React from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ClusterContactStatus, TimeEntryDto, TimeEntrySource } from 'src/api/v2';
import { formatDateToNOString } from 'src/utils/FormatValue';
import { formatHours } from 'src/utils/format';
import { CurrencyCell } from 'src/components/DataTableV3/components/formatters/Currency';
import { ROUTES } from 'src/navigation';

const columnHelper = createColumnHelper<TimeEntryDto>();

const useEventParticpantColumns = () => {
  const { t: tCommon } = useTranslation();

  const getContactStatus = (status: ClusterContactStatus | undefined) => {
    switch (status) {
      case ClusterContactStatus.Active:
        return tCommon('active');
      case ClusterContactStatus.InActive:
        return tCommon('inactive');
      case ClusterContactStatus.PendingApproval:
        return tCommon('pendingApproval');
      default:
        return '';
    }
  };

  const getTimeEntrySource = (source: string) => {
    switch (source) {
      case TimeEntrySource.Import:
        return tCommon('imported');
      case TimeEntrySource.Link:
        return tCommon('registrationLink');
      case TimeEntrySource.Manual:
        return tCommon('manually');
      default:
        return tCommon('unspecified');
    }
  };

  const getTimeEntryStatus = (status: string) => {
    switch (status) {
      case TimeEntrySource.Import:
        return tCommon('imported');
      case TimeEntrySource.Link:
        return tCommon('registrationLink');
      case TimeEntrySource.Manual:
        return tCommon('manually');
      default:
        return '';
    }
  };

  return [
    columnHelper.accessor(
      (row) => formatDateToNOString(row.createdOn, false, '{date}.{month}.{year}', true),
      {
        id: 'added',
        cell: (info) => info.getValue(),
        meta: {
          hide: true,
        },
      },
    ),
    columnHelper.accessor((row) => row.clusterContactName, {
      id: 'resourceBaseContact',
      cell: ({
        getValue,
        row: {
          original: { clusterContactId },
        },
      }) => <Link to={ROUTES.RESOURCE_BASE.CONTACTS.DETAILS(clusterContactId || '').BASE}>{getValue()}</Link>,
    }),
    columnHelper.accessor((row) => row.clusterContactEmailAddress, {
      id: 'email',
      cell: (info) => info.getValue(),
      meta: {
        hide: true,
      },
    }),
    columnHelper.accessor((row) => row.clusterMemberName, {
      id: 'memberCompany',
      cell: ({
        getValue,
        row: {
          original: { clusterMemberId },
        },
      }) => <Link to={ROUTES.RESOURCE_BASE.MEMBERS.DETAILS(clusterMemberId || '').BASE}>{getValue()}</Link>,
    }),
    columnHelper.accessor((row) => getContactStatus(row.clusterContactStatus), {
      id: 'contactStatus',
      cell: (info) => info.getValue(),
      meta: {
        hide: true,
      },
    }),
    columnHelper.accessor(
      (row) => formatDateToNOString(row.reportedDate, false, '{date}.{month}.{year}', true),
      {
        id: 'eventDate',
        cell: (info) => info.getValue(),
      },
    ),
    columnHelper.accessor((row) => formatHours(row.hours), {
      id: 'hours',
      cell: (info) => info.getValue(),
      meta: {
        hide: true,
      },
    }),
    columnHelper.accessor((row) => row.hourRate, {
      id: 'hourRate',
      cell: (info) => info.getValue(),
      meta: {
        hide: true,
      },
    }),
    columnHelper.accessor(
      (row) => {
        if (row) {
          return (row.hours * row.hourRate).toFixed(0);
        }
        return null;
      },
      {
        id: 'selfEffort',
        cell: (info) => <CurrencyCell currency={parseFloat(info.getValue() || '')} />,
        meta: {
          hide: true,
        },
      },
    ),
    columnHelper.accessor((row) => getTimeEntrySource(row.source), {
      id: 'source',
      cell: (info) => info.getValue(),
      meta: {
        hide: true,
      },
    }),
    columnHelper.accessor((row) => getTimeEntryStatus(row.status), {
      id: 'selfEffortStatus',
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor((row) => row.description, {
      id: 'description',
      cell: (info) => info.getValue(),
      meta: {
        hide: true,
      },
    }),
  ];
};

export default useEventParticpantColumns;
